import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
// icons
import FilterListIcon from "@mui/icons-material/FilterList"
import { Chip, Stack, TextField, useTheme } from "@mui/material"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import FormControlLabel from "@mui/material/FormControlLabel"
import IconButton from "@mui/material/IconButton"
import Paper from "@mui/material/Paper"
import Switch from "@mui/material/Switch"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TablePagination from "@mui/material/TablePagination"
import TableRow from "@mui/material/TableRow"
import TableSortLabel from "@mui/material/TableSortLabel"
import Toolbar from "@mui/material/Toolbar"
import Tooltip from "@mui/material/Tooltip"
// @mui
import { visuallyHidden } from "@mui/utils"
import * as React from "react"
import { useEffect, useState } from "react"
import { SEO } from "../../components/SEO"
import { PATHS } from "../../constants/paths"
import Typography from "@mui/material/Typography"
import useMediaQuery from "@mui/material/useMediaQuery"
import { getTransactions } from "../../api/transactions"
import { useAppSelector } from "../../store"
import { web3BlockExplorer } from "../../constants/constants"

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1
  }
  if (b[orderBy] > a[orderBy]) {
    return 1
  }
  return 0
}

function getComparator(order, orderBy) {
  return order === "ask"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) {
      return order
    }
    return a[1] - b[1]
  })
  return stabilizedThis.map((el) => el[0])
}

const headCells = [
  {
    id: "createdAt",
    label: "Date",
  },
  {
    id: "type",
    label: "Type",
  },
  {
    id: "amount",
    label: "Amount",
  },
  {
    id: "status",
    label: "Status",
  },
  {
    id: "details",
    label: "Details",
  },
  {
    id: "hash",
    label: "Hash",
  },
  // {
  //   id: "actions",
  //   label: "Actions",
  // },
]

function EnhancedTableHead(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property)
  }

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align="center"
            // align={headCell.numeric ? "right" : "center"}
            // padding={headCell.disablePadding ? "none" : "normal"}
            padding={"none"}
            sx={{ py: 1, px: 0 }}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

const EnhancedTableToolbar = () => {
  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
      }}
    >
      <Stack direction={"row"} spacing={2}>
        <TextField
          label="Enter text to search..."
          variant="outlined"
          size={"small"}
          sx={{ minWidth: 300 }}
        />
        <Tooltip title="Filter list">
          <IconButton>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      </Stack>
    </Toolbar>
  )
}

export const TransactionsPage = () => {
  const theme = useTheme()
  const { user } = useAppSelector((state) => state.user)

  const [rows, setRows] = useState([])

  useEffect(() => {
    if (user.address) {
      loadTransactions()
    }
  }, [user.address])

  const loadTransactions = async () => {
    try {
      const data = await getTransactions(user.sessionToken)
      setRows([])
      data.forEach((item) => {
        setRows((prev) => [...prev, item])
      })
    } catch (error) {
      console.error("Error loading commanders:", error)
    }
  }

  const [order, setOrder] = useState("ask")
  const [orderBy, setOrderBy] = useState("id")
  const [selected, setSelected] = useState([])
  const [page, setPage] = useState(0)
  const [dense, setDense] = useState(false)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc"
    setOrder(isAsc ? "desc" : "asc")
    setOrderBy(property)
  }

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.id)
      setSelected(newSelected)
      return
    }
    setSelected([])
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleChangeDense = (event) => {
    setDense(event.target.checked)
  }

  const isSelected = (id) => selected.indexOf(id) !== -1

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0

  const isDownSm = useMediaQuery("(max-width: 600px)")

  return (
    <Box>
      <SEO
        title="Zombie TD | Transactions - Track Your Activity"
        description="View your transaction history for token purchases, staking, and NFT operations."
        ogTitle="Zombie TD | Transactions"
        ogDescription="Track all your transactions and activities within the Zombie TD ecosystem."
        ogUrl={PATHS.TRANSACTIONS.PATH}
      />
      <Box sx={{ width: "100%" }}>
        <Typography variant="h1" mb={2}>
          Transactions
        </Typography>
        {rows.length > 0 ? (
          <>
            <Paper
              sx={{
                width: isDownSm ? "calc(100vw - 32px)" : "100%",
                mb: 2,
                overflowX: "scroll",
              }}
            >
              <EnhancedTableToolbar />
              <TableContainer>
                <Table
                  sx={{ minWidth: 750 }}
                  aria-labelledby="tableTitle"
                  size={dense ? "small" : "medium"}
                >
                  <EnhancedTableHead
                    numSelected={selected.length}
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={handleRequestSort}
                    rowCount={rows.length}
                  />
                  <TableBody>
                    {stableSort(rows, getComparator(order, orderBy))
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((row, index) => {
                        const isItemSelected = isSelected(row.id)
                        const labelId = `enhanced-table-checkbox-${index}`

                        return (
                          <TableRow
                            hover
                            aria-checked={isItemSelected}
                            tabIndex={-1}
                            key={row.hash}
                          >
                            <TableCell sx={{ textAlign: "center" }}>
                              {row.created_at}
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              {row.type}
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              {row.amount}
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              <Chip
                                label={row.status}
                                sx={{
                                  background:
                                    row.status === "error"
                                      ? theme.palette.error.main
                                      : row.status === "success"
                                        ? theme.palette.success.light
                                        : theme.palette.warning.main,
                                  textTransform: "capitalize",
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ textAlign: "left" }}>
                              {row.details}
                            </TableCell>
                            <TableCell
                              sx={{ textAlign: "center" }}
                              component="th"
                              id={labelId}
                              scope="row"
                            >
                              {row.hash ? (
                                <a
                                  href={`${web3BlockExplorer}${row.hash}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {row.hash.slice(0, 8)}...
                                </a>
                              ) : (
                                "—"
                              )}
                            </TableCell>
                            {/*<TableCell>*/}
                            {/*  <Button*/}
                            {/*    variant={"outlined"}*/}
                            {/*    endIcon={<ArrowForwardIcon />}*/}
                            {/*    disabled*/}
                            {/*  >*/}
                            {/*    Details*/}
                            {/*  </Button>*/}
                            {/*</TableCell>*/}
                          </TableRow>
                        )
                      })}
                    {emptyRows > 0 && (
                      <TableRow
                        style={{
                          height: (dense ? 33 : 53) * emptyRows,
                        }}
                      >
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25]}
                component="div"
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
            <FormControlLabel
              control={<Switch checked={dense} onChange={handleChangeDense} />}
              label="Dense padding"
            />
          </>
        ) : (
          <span className="primary">You don't have any transactions yet</span>
        )}
      </Box>
    </Box>
  )
}
